/**
 * FX HedgePool Inc. Proprietary and Confidential
 *
 * This source code and the algorithms implemented herein are the sole property of FX HedgePool, Inc.,
 * are confidential information, and are trade secrets of FX HedgePool, Inc.
 * Redistribution or use of this source code or the algorithms implemented herein, in whole or in part,
 * without the express written permission of FX HedgePool, Inc. is strictly prohibited.
 */

import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import {
  BehaviorSubject,
  catchError,
  of,
  Subscription,
  switchMap,
  tap,
  throwError,
  timer,
} from 'rxjs';
import {
  APPLICATION_CONFIG,
  ApplicationConfig,
} from '../models/application-config';
import {
  EditableCreditLimitEntities,
  ObjectStorage,
} from '../models/object-storage.model';
import { RoleId, RoleMap } from '../models/roles.model';
import { DecodedToken } from './token/models';
import { TokenHelperService } from './token/token-helper.service';
import { TokenService } from './token/token.service';

declare let gtag: Function;

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private storage: ObjectStorage = {};
  private appConfig: ApplicationConfig = inject(APPLICATION_CONFIG);

  private readonly tokenService = inject(TokenService);
  private readonly tokenHelperService = inject(TokenHelperService);
  readonly accessToken$ = new BehaviorSubject<string | null>(
    this.tokenService.getAccessToken() ?? null,
  );
  private tokenRefreshSub?: Subscription;

  constructor(private readonly router: Router) {
    const jwtPayload = this.getJWTPayload();
    if (jwtPayload) {
      this.setSentryMetadata({
        userId: jwtPayload['userId'],
        username: jwtPayload['username'],
        email: jwtPayload['email'],
      });
    }
  }

  public setToken(token: string): void {
    this.tokenService.setAccessToken(token);

    this.accessToken$.next(token);
  }

  public getToken(): string {
    const token = this.tokenService.getAccessToken();
    if (!token && this.router.url.indexOf('/login') < 0) {
      this.router.navigate(['/login']);
    }
    return token;
  }

  public getRefreshToken(): string {
    const token = this.tokenService.getRefreshToken();
    if (!token && this.router.url.indexOf('/login') < 0) {
      this.router.navigate(['/login']);
    }
    return token;
  }

  public removeUserSession(): void {
    this.accessToken$.next(null);
    this.tokenService.removeTokens();
    this.remove('jwtPayload');
  }

  public setJWTPayload(jwtPayload: DecodedToken): void {
    this.set('jwtPayload', jwtPayload);
  }

  public getJWTPayload(): DecodedToken {
    return this.get('jwtPayload');
  }

  public getUserId(): string {
    const jwt = this.get('jwtPayload');
    return jwt['userId'];
  }

  public setUserSession(response: { token: string; refreshToken: string }) {
    this.tokenService.setRefreshToken(response.refreshToken);
    this.setToken(response.token);

    const jwtPayload = this.tokenHelperService.decodeToken(response.token);

    if (jwtPayload === null) {
      return;
    }

    this.setJWTPayload(jwtPayload);

    gtag('js', new Date());
    gtag('config', this.appConfig.googleAnalyticsId, {
      user_id: jwtPayload['userId'],
    });
    gtag('set', 'user_properties', {
      UserId: jwtPayload['userId'],
    });

    this.setSentryMetadata({
      userId: jwtPayload['userId'],
      username: jwtPayload['username'],
      email: jwtPayload['email'],
    });
  }

  public isAdmin(): boolean {
    return this.getJWTPayload()['custom:entityType'] === 'fxhp';
  }

  public isCreditBank(): boolean {
    return this.getJWTPayload()['custom:entityType'] === 'credit_bank';
  }

  public isMember(): boolean {
    return this.getJWTPayload()['custom:entityType'] === 'member';
  }

  public canViewDailyMatch(entityId: string): boolean {
    return this.get('members')[entityId].canViewDailyMatch;
  }

  public isNonFXHPAdmin(): boolean {
    return (
      this.getJWTPayload()['custom:entityType'] !== 'fxhp' &&
      this.hasRole(RoleId.Admin)
    );
  }

  public getEntityType(): string {
    return this.getJWTPayload()['custom:entityType'];
  }

  public isFXHPViewer(): boolean {
    return (
      this.getJWTPayload()['custom:entityType'] === 'fxhp' &&
      this.hasRole(RoleId.Viewer)
    );
  }
  public isFXHPRiskManager(): boolean {
    return (
      this.getJWTPayload()['custom:entityType'] === 'fxhp' &&
      this.hasRole(RoleId.RiskManager)
    );
  }

  public isFXHPAdmin(): boolean {
    return (
      this.getJWTPayload()['custom:entityType'] === 'fxhp' &&
      this.hasRole(RoleId.Admin)
    );
  }
  canEditBlotter(): boolean {
    return (
      this.isFXHPAdmin() ||
      this.isBlotterTrader() ||
      this.isAlgoBlotterUser() ||
      this.isFixingOrderTrader()
    );
  }
  public canEditPositions(): boolean {
    return (
      this.hasRole(RoleId.Admin) ||
      this.hasRole(RoleId.Trader) ||
      this.hasRole(RoleId.BlotterTrader) ||
      this.hasRole(RoleId.BlotterAlgoTrader)
    );
  }

  public isRiskManager(): boolean {
    return this.hasRole(RoleId.RiskManager);
  }

  public isTrader(): boolean {
    return this.hasRole(RoleId.Trader);
  }

  public isBlotterTrader(): boolean {
    return (
      this.hasRole(RoleId.BlotterTrader) ||
      this.hasRole(RoleId.BlotterAlgoTrader) ||
      this.isFXHPAdmin()
    );
  }
  public isFixingOrderTrader(): boolean {
    return this.hasRole(RoleId.FixingOrderTrader);
  }

  public canEditCreditRiskManager(selectedEntityId: string): boolean {
    const fxhpAdmin =
      this.isFXHPAdmin() &&
      (selectedEntityId === EditableCreditLimitEntities.SCBMEMBER ||
        selectedEntityId === EditableCreditLimitEntities.TDMEMBER);
    const userAdmin = this.hasRole(RoleId.Admin) && !this.isFXHPAdmin();
    return fxhpAdmin || userAdmin || this.isRiskManager();
  }

  public isAlgoBlotterUser(): boolean {
    return this.hasRole(RoleId.BlotterAlgoTrader) || this.isFXHPAdmin();
  }

  public canAccessAudit(): boolean {
    return (
      (this.getJWTPayload()['custom:entityType'] !== 'fxhp' &&
        this.hasRole(RoleId.Admin)) ||
      this.hasRole(RoleId.RiskManager) ||
      this.hasRole(RoleId.Viewer)
    );
  }

  private set(key: string, value: any): void {
    this.storage[key] = value;
    sessionStorage.setItem(key, JSON.stringify(value));
  }

  private get(key: string): any {
    try {
      const value = this.storage[key];
      if (value) {
        return value;
      }
      return JSON.parse(sessionStorage.getItem(key) ?? '');
    } catch (e) {
      return false;
    }
  }

  private setSentryMetadata({
    userId,
    username,
    email,
  }: {
    userId: string;
    username: string;
    email: string;
  }) {
    Sentry.setUser({
      id: userId,
      username,
      email,
    });
  }

  private remove(key: string): void {
    delete this.storage[key];
    sessionStorage.removeItem(key);
  }

  private hasRole(role: RoleId): boolean {
    const rolesMap: RoleMap = JSON.parse(this.getJWTPayload()['custom:roles']);
    let roleExists = false;
    Object.keys(rolesMap).forEach((key) => {
      if (rolesMap[key].includes(role)) {
        roleExists = true;
      }
    });
    return roleExists;
  }

  public logout(): void {
    this.removeUserSession();
    sessionStorage.clear();
    this.tokenRefreshSub?.unsubscribe();
    this.router.navigate(['/login']);
  }

  public setupTokenRefreshTimer(): void {
    const thirtyMinutes = 30 * 60 * 1000;
    this.tokenRefreshSub?.unsubscribe();

    this.tokenRefreshSub = timer(thirtyMinutes, thirtyMinutes)
      .pipe(switchMap(() => this.checkToken()))
      .subscribe();
  }

  public checkToken() {
    const previousTokenExpired = this.tokenService.isTokenExpired(
      this.tokenService.getAccessToken(),
    );

    return this.tokenService.tokenGetter().pipe(
      switchMap((token) => {
        if (token === null) {
          return throwError(() => new Error('Unable to refresh token.'));
        }

        return of(token);
      }),
      tap(({ token, refreshToken }) => {
        if (previousTokenExpired) {
          this.setUserSession({
            token,
            refreshToken,
          });
        }
      }),
      catchError((error) => {
        this.logout();

        return throwError(() => error);
      }),
    );
  }
}
